/**
 * Post Generation State Management
 * Feature 014: Unified Multi-AI Post Generation Flow
 * T036-T037: Reducer for managing complex post generation state
 */

import { useReducer } from 'react';
import { IPostGenerationState, PostGenerationAction } from '../@types/postGeneration';
import { Id } from '../../convex/_generated/dataModel';

/**
 * T036: Initial state for post generation
 */
const initialState: IPostGenerationState = {
  phase: 'idle',
  researchResults: null,
  researchCached: false,
  researchLoading: false,
  researchError: null,
  selectedModels: [],
  generationStatus: {},
  generatedPosts: {},
  selectedVariantModel: null,
  isSelecting: false,
  postId: null,
};

/**
 * T037: Post Generation Reducer
 * Handles all state transitions for research + 3-model generation flow
 */
function postGenerationReducer(
  state: IPostGenerationState,
  action: PostGenerationAction
): IPostGenerationState {
  switch (action.type) {
    case 'INIT_POST':
      return {
        ...initialState,
        postId: action.payload.postId,
        phase: 'idle',
        // CRITICAL FIX: Preserve selectedModels and generationStatus from current state
        // These were set by SET_SELECTED_MODELS and should NOT be reset when initializing post
        selectedModels: state.selectedModels,
        generationStatus: state.generationStatus,
      };

    case 'START_RESEARCH':
      return {
        ...state,
        phase: 'research',
        researchLoading: true,
        researchError: null,
      };

    case 'RESEARCH_SUCCESS':
      return {
        ...state,
        phase: 'research',
        researchLoading: false,
        researchResults: action.payload.researchResults,
        researchCached: action.payload.cached,
        researchError: null,
      };

    case 'RESEARCH_ERROR':
      return {
        ...state,
        phase: 'error',
        researchLoading: false,
        researchError: action.payload.error,
      };

    case 'SET_SELECTED_MODELS': {
      // Dynamically build generationStatus object based on selected models
      const generationStatus: Record<string, 'idle' | 'loading' | 'completed' | 'error'> = {};
      action.payload.models.forEach(model => {
        generationStatus[model] = 'idle';
      });

      return {
        ...state,
        selectedModels: action.payload.models,
        generationStatus,
      };
    }

    case 'START_GENERATION':
      return {
        ...state,
        phase: 'generation',
        generatedPosts: {},
      };

    case 'MODEL_GENERATION_START':
      return {
        ...state,
        generationStatus: {
          ...state.generationStatus,
          [action.payload.modelKey]: 'loading',
        },
      };

    case 'MODEL_GENERATION_SUCCESS':
      return {
        ...state,
        generationStatus: {
          ...state.generationStatus,
          [action.payload.modelKey]: 'completed',
        },
        generatedPosts: {
          ...state.generatedPosts,
          [action.payload.modelKey]: action.payload.postData,
        },
      };

    case 'MODEL_GENERATION_ERROR':
      return {
        ...state,
        generationStatus: {
          ...state.generationStatus,
          [action.payload.modelKey]: 'error',
        },
        generatedPosts: {
          ...state.generatedPosts,
          [action.payload.modelKey]: {
            content: '',
            model: action.payload.modelKey,
            timestamp: Date.now(),
            cached: false,
            error: action.payload.error,
          },
        },
      };

    case 'ALL_MODELS_COMPLETE':
      return {
        ...state,
        phase: 'completed',
      };

    case 'START_VARIANT_SELECTION':
      return {
        ...state,
        isSelecting: true,
      };

    case 'VARIANT_SELECTION_SUCCESS':
      return {
        ...state,
        selectedVariantModel: action.payload.modelKey,
        isSelecting: false,
      };

    case 'VARIANT_SELECTION_ERROR':
      return {
        ...state,
        isSelecting: false,
        researchError: action.payload.error,
      };

    case 'RESET':
      return initialState;

    default:
      return state;
  }
}

/**
 * Custom hook for post generation state
 * Returns state and dispatch function
 */
export function usePostGenerationState(postId?: Id<'posts'>) {
  const [state, dispatch] = useReducer(postGenerationReducer, {
    ...initialState,
    postId: postId || null,
  });

  return { state, dispatch };
}
